import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ColumnType } from "antd/lib/table";
import Skeleton from "antd/lib/skeleton";
import useDispatch from "@/Components/Utils/useDispatch";
import ZDrawer from "@molecules/ZDrawer";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import {
	TaxRegion,
	TaxCode,
	TaxCodeLevel,
	AddTaxCategoryFormValues,
	AddTaxCategoryRequest,
	UserRole,
	PricingPlanNames,
	TaxCategory
} from "@/Contracts/Users";
import UsersActionCreators from "@/Actions/User";
import SettingsActionCreators from "@/Actions/Settings";
import ZMessage from "@molecules/ZMessage";
import { ITaxCategoryDrawerProps } from "./interface";
import Form from "antd/lib/form";
import { getTaxCategoryColumns } from "@templates/ZTaxListPage/molecules/ZTaxCategoryColumns";
import ZTaxCategoryForm from "@templates/ZTaxListPage/molecules/ZTaxCategoryForm";
import { ITaxCategoryFormProps } from "@templates/ZTaxListPage/molecules/ZTaxCategoryForm/interface";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { FormKeys } from "@/Contracts";

const TaxCategoryDrawer: React.FC<ITaxCategoryDrawerProps> = ({
	visible = false,
	onClose,
	currentActiveTaxCategory,
	isEditMode = false,
	handleTaxCategoryDrawerOnSubmit
}) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const [taxCodes, setTaxCodes] = useState<TaxCode[]>(new Array<TaxCode>());
	const [isLoadingTaxCodes, setIsLoadingTaxCodes] = useState<boolean>(false);

	const {
		isLoading,
		userData,
		taxRegionsList,
		isFormEdited
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.settings.isLoading || state.user.isLoading,
			userData: state.user.userData,
			taxRegionsList: state.user.taxRegionsList,
			isFormEdited: state.settings.isFormEdited
		};
	});

	const initializeEditTaxCategoryDrawer = () => {
		const tempTaxCodes = new Array<TaxCode>();
		taxRegionsList?.map(region => {
			const taxCategory = currentActiveTaxCategory?.tax_codes?.find(
				taxCode => taxCode.region?.id === region.id
			);
			if (taxCategory?.levels) {
				const tempLevels = taxCategory.levels.map(level => ({
					...level
				}));
				tempTaxCodes.push({
					region,
					region_id: region.id,
					levels: tempLevels
				});
			} else {
				tempTaxCodes.push({
					region,
					region_id: region.id,
					levels: new Array<TaxCodeLevel>({
						name: "",
						tax_percentage: 0
					})
				});
			}
		});
		setTaxCodes(tempTaxCodes);
		form.setFieldsValue({
			name: currentActiveTaxCategory?.name,
			identifier: currentActiveTaxCategory?.identifier,
			default_for_types:
				currentActiveTaxCategory?.default_for_types ?? [],
			disabled: !currentActiveTaxCategory?.disabled
		});
	};

	const initializeCreateTaxCategoryDrawer = () => {
		const tempTaxCodes = new Array<TaxCode>();
		taxRegionsList?.map(region => {
			tempTaxCodes.push({
				region,
				region_id: region.id,
				levels: new Array<TaxCodeLevel>({ name: "", tax_percentage: 0 })
			});
		});
		setTaxCodes(tempTaxCodes);
	};

	useEffect(() => {
		if (visible && taxRegionsList) {
			if (isEditMode && currentActiveTaxCategory) {
				initializeEditTaxCategoryDrawer();
			} else if (!isEditMode) {
				initializeCreateTaxCategoryDrawer();
			}
		}
	}, [visible]);

	const addTaxCodeLevel = (region: TaxRegion) => {
		const tempTaxCodes = [...taxCodes];
		const taxCode = tempTaxCodes.find(
			taxCode => taxCode.region?.id === region.id
		);
		if (taxCode) {
			taxCode?.levels?.push({
				name: "",
				tax_percentage: 0
			});
			setTaxCodes(tempTaxCodes);
		}
	};

	const editTaxCodeLevelName = (
		taxCodeName: string,
		region: TaxRegion,
		taxCodeLevelIndex: number
	) => {
		const tempTaxCodes = [...taxCodes];
		const taxCode = tempTaxCodes.find(
			taxCode => taxCode.region?.id === region.id
		);
		if (taxCode && taxCodeLevelIndex > -1) {
			taxCode.levels[taxCodeLevelIndex].name = taxCodeName;
			setTaxCodes(tempTaxCodes);
		}
	};

	const editTaxCodeLevelRate = (
		taxCodeRate: number,
		region: TaxRegion,
		taxCodeLevelIndex: number
	) => {
		const tempTaxCodes = [...taxCodes];
		const taxCode = tempTaxCodes.find(
			taxCode => taxCode.region?.id === region.id
		);
		if (taxCode && taxCodeLevelIndex > -1) {
			taxCode.levels[taxCodeLevelIndex].tax_percentage = taxCodeRate;
			setTaxCodes(tempTaxCodes);
		}
	};

	const removeTaxCodeLevel = (
		region: TaxRegion,
		taxCodeLevelIndex: number
	) => {
		const tempTaxCodes = [...taxCodes];
		const taxCode = tempTaxCodes.find(
			taxCode => taxCode.region?.id === region.id
		);
		if (taxCode) {
			taxCode.levels?.splice(taxCodeLevelIndex, 1);
			setIsLoadingTaxCodes(true);
			setTaxCodes([]);
			setTimeout(() => {
				setTaxCodes(tempTaxCodes);
				setIsLoadingTaxCodes(false);
			}, 0);
		}
	};

	const taxCategoryCodesColumns: ColumnType<TaxRegion>[] =
		getTaxCategoryColumns(
			taxCodes,
			form,
			isLoading,
			addTaxCodeLevel,
			editTaxCodeLevelName,
			editTaxCodeLevelRate,
			removeTaxCodeLevel
		);

	const handleClose = () => {
		form.resetFields();
		setTaxCodes(new Array<TaxCode>());
		onClose && onClose();
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddEditTaxCategory)
		);
	};

	const closeTaxCategoryDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditTaxCategory]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} tax category?`,
				onOk: handleClose
			});
		}
	};

	const addTaxCategory = async (
		addTaxCategoryFormValues: AddTaxCategoryFormValues
	) => {
		dispatch(SettingsActionCreators.showSettingsLoader());
		try {
			const tempTaxCodes = new Array<TaxCode>();
			if (addTaxCategoryFormValues.tax_codes) {
				Object.keys(addTaxCategoryFormValues.tax_codes)?.forEach(
					regionId => {
						const taxCodeLevels =
							addTaxCategoryFormValues.tax_codes[regionId];
						const taxRegion = taxRegionsList?.find(
							region => region.id === regionId
						) as TaxRegion;
						const taxCode: TaxCode = {
							region: taxRegion,
							region_id: regionId,
							levels: Object.values(taxCodeLevels)
						};
						tempTaxCodes.push(taxCode);
					}
				);
			}
			const addTaxCategoryRequest: AddTaxCategoryRequest = {
				name: addTaxCategoryFormValues.name,
				identifier: addTaxCategoryFormValues.identifier,
				default_for_types:
					addTaxCategoryFormValues?.default_for_types ?? [],
				disabled: !addTaxCategoryFormValues.disabled,
				tax_codes: tempTaxCodes
			};
			const newtaxCategory = await dispatch(
				UsersActionCreators.addTaxCategory(addTaxCategoryRequest)
			);
			handleTaxCategoryDrawerOnSubmit &&
				handleTaxCategoryDrawerOnSubmit(
					newtaxCategory.addTaxCategory.id
				);
			await dispatch(UsersActionCreators.getTaxCategoriesList());
			await dispatch(UsersActionCreators.getUserCompany());
			handleClose();
			ZMessage.success("Tax Category added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SettingsActionCreators.hideSettingsLoader());
		}
	};

	const editTaxCategory = async (
		editTaxCategoryFormValues: AddTaxCategoryFormValues
	) => {
		dispatch(SettingsActionCreators.showSettingsLoader());
		try {
			if (!currentActiveTaxCategory) {
				console.warn("No Active tax Category::");
				dispatch(SettingsActionCreators.hideSettingsLoader());
				return;
			}
			const tempTaxCodes = new Array<TaxCode>();

			if (editTaxCategoryFormValues.tax_codes) {
				Object.keys(editTaxCategoryFormValues.tax_codes)?.forEach(
					regionId => {
						const taxCodeLevels =
							editTaxCategoryFormValues.tax_codes[regionId];
						const taxRegion = taxRegionsList?.find(
							region => region.id === regionId
						) as TaxRegion;
						const taxCode: TaxCode = {
							region: taxRegion,
							region_id: regionId,
							levels: Object.values(taxCodeLevels)
						};
						tempTaxCodes.push(taxCode);
					}
				);
			}
			const editTaxCategoryRequest: TaxCategory = {
				...currentActiveTaxCategory,
				name: editTaxCategoryFormValues.name,
				identifier: editTaxCategoryFormValues.identifier,
				default_for_types:
					editTaxCategoryFormValues?.default_for_types ?? [],
				disabled: !editTaxCategoryFormValues.disabled,
				tax_codes: tempTaxCodes
			};
			await dispatch(
				UsersActionCreators.editTaxCategory(editTaxCategoryRequest)
			);
			await dispatch(UsersActionCreators.getTaxCategoriesList());
			await dispatch(UsersActionCreators.getUserCompany());
			handleClose();
			ZMessage.success("Tax Category edited successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SettingsActionCreators.hideSettingsLoader());
		}
	};

	const isFormAllowed = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.AdminRole
	);

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditTaxCategory] &&
			dispatch(
				SettingsActionCreators.setFormEdited(
					FormKeys.AddEditTaxCategory
				)
			);
	};

	const formProps: ITaxCategoryFormProps = {
		form,
		isLoading,
		taxRegionsList,
		onSubmit: isEditMode ? editTaxCategory : addTaxCategory,
		handleClose: closeTaxCategoryDrawer,
		taxCategoryCodesColumns,
		tableLoading: isLoadingTaxCodes,
		handleFormChange
	};

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={closeTaxCategoryDrawer}
			title={`${isEditMode ? "Edit" : "Add"} Tax Category`}
			removeDefaultPadding={true}
		>
			{isFormAllowed && (
				<Skeleton
					loading={!userData || userData.id.length === 0}
					active
					paragraph={{ rows: 12 }}
				>
					<ZTaxCategoryForm {...formProps} />
				</Skeleton>
			)}
		</ZDrawer>
	);
};

export default TaxCategoryDrawer;
